/**
 * Centralized Logging Service
 * Provides environment-aware logging with configurable levels
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SILENT = 4
}

interface LogContext {
  component?: string;
  userId?: string;
  action?: string;
  metadata?: Record<string, any>;
}

class LoggingService {
  private static instance: LoggingService;
  private logLevel: LogLevel;
  private isDevelopment: boolean;

  private constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.ERROR;
  }

  static getInstance(): LoggingService {
    if (!LoggingService.instance) {
      LoggingService.instance = new LoggingService();
    }
    return LoggingService.instance;
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private formatMessage(level: string, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] ${level}:`;
    
    if (context) {
      const contextStr = Object.entries(context)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => `${key}=${JSON.stringify(value)}`)
        .join(' ');
      return contextStr ? `${prefix} [${contextStr}] ${message}` : `${prefix} ${message}`;
    }
    
    return `${prefix} ${message}`;
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('DEBUG', message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage('INFO', message, context));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', message, context));
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorMessage = this.formatMessage('ERROR', message, context);
      console.error(errorMessage, error);
      
      // In production, you might want to send errors to a monitoring service
      if (!this.isDevelopment && error) {
        // Example: sendToMonitoringService(errorMessage, error, context);
      }
    }
  }

  // Performance logging
  time(label: string): void {
    if (this.isDevelopment && this.shouldLog(LogLevel.DEBUG)) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.isDevelopment && this.shouldLog(LogLevel.DEBUG)) {
      console.timeEnd(label);
    }
  }

  // Group logging for complex operations
  group(label: string): void {
    if (this.isDevelopment && this.shouldLog(LogLevel.DEBUG)) {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (this.isDevelopment && this.shouldLog(LogLevel.DEBUG)) {
      console.groupEnd();
    }
  }

  // Table logging for data structures
  table(data: any, context?: LogContext): void {
    if (this.isDevelopment && this.shouldLog(LogLevel.DEBUG)) {
      if (context) {
        this.debug('Table data:', context);
      }
      console.table(data);
    }
  }
}

// Export singleton instance
export const logger = LoggingService.getInstance();

// Convenience exports for common patterns
export const logPerformance = {
  start: (label: string) => logger.time(label),
  end: (label: string) => logger.timeEnd(label),
};

export const logComponent = (componentName: string) => ({
  debug: (message: string, metadata?: Record<string, any>) => 
    logger.debug(message, { component: componentName, metadata }),
  info: (message: string, metadata?: Record<string, any>) => 
    logger.info(message, { component: componentName, metadata }),
  warn: (message: string, metadata?: Record<string, any>) => 
    logger.warn(message, { component: componentName, metadata }),
  error: (message: string, error?: Error, metadata?: Record<string, any>) => 
    logger.error(message, error, { component: componentName, metadata }),
});

export const logUser = (userId: string) => ({
  debug: (message: string, action?: string, metadata?: Record<string, any>) => 
    logger.debug(message, { userId, action, metadata }),
  info: (message: string, action?: string, metadata?: Record<string, any>) => 
    logger.info(message, { userId, action, metadata }),
  warn: (message: string, action?: string, metadata?: Record<string, any>) => 
    logger.warn(message, { userId, action, metadata }),
  error: (message: string, error?: Error, action?: string, metadata?: Record<string, any>) => 
    logger.error(message, error, { userId, action, metadata }),
});